import React, {
  Fragment,
  memo,
  useCallback,
  useState,
  createContext,
} from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useQueryParam } from 'react-resource-router';

import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { RenderProps } from 'src/components/list-keyboard-shortcuts';
import { withRunPipelineModal } from 'src/components/pipelines/pages/modals/run-pipeline-modal';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { MetricSender, BRANCH_LIST_LOAD } from 'src/metrics';
import {
  onToggleBulkDeleteSelectionMode,
  openBulkDeleteBranchesDialog,
} from 'src/redux/branches';
import { BranchingModelState } from 'src/redux/branches/reducers/branch-list-reducer';
import RepositoryAccessGuard from 'src/sections/repository/components/repository-access-guard';
import { SyncBranchDialog } from 'src/sections/repository/sections/branches/components/dialogs/sync-branch-dialog-functional';
import { REPO_EVENT_SOURCES } from 'src/sections/repository/types';
import { SyncStrategy } from 'src/types/pull-request';
import { publishUiEvent } from 'src/utils/analytics/publish';
import { CaptureSentryException } from 'src/utils/capture-sentry-exception';

import { DetailedBranch, Sort } from '../types';

import messages from './branch-list.i18n';
import BranchTable from './branch-table/branch-table';

export type BranchListProps = {
  sort: Sort;
  isLoading: boolean;
  isError: boolean;
  branches: DetailedBranch[];
  mainBranch: DetailedBranch;
  isStale: boolean;
  onLoadPage: () => void;
  onSortChange: (sort: Sort) => void;
  renderPagination?: (() => JSX.Element) | null;
  onToggleBranchSelect: (branch: DetailedBranch) => void;
  onToggleBranchSelectAllInPage: (isSelected: boolean) => void;
  selectedBranches: DetailedBranch[];
  isInSelectionMode: boolean;
  branchingModel: BranchingModelState;
  exception?: Error;
};

export const SyncStrategyContext = createContext<
  React.Dispatch<React.SetStateAction<SyncStrategy[] | undefined>> | undefined
>(undefined);

export const BranchList = ({
  sort,
  isLoading,
  isError,
  exception,
  branches,
  mainBranch,
  isStale,
  onLoadPage,
  onSortChange,
  focusedRowIndex,
  renderPagination,
  onToggleBranchSelect,
  selectedBranches,
  onToggleBranchSelectAllInPage,
  isInSelectionMode,
  branchingModel,
}: BranchListProps & RenderProps) => {
  const branchTableProps = {
    sort,
    onSortChange,
    isError,
    isLoading,
    branches,
    mainBranch,
    isStale,
    focusedRowIndex,
    onLoadPage,
    renderPagination,
    onToggleBranchSelect,
    selectedBranches,
    onToggleBranchSelectAllInPage,
    isInSelectionMode,
    branchingModel,
  };

  const dispatch = useDispatch();
  const deleteMultipleBranchesClick = useCallback(() => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'deleteSelectedBranches',
      source: REPO_EVENT_SOURCES.BRANCHES_SCREEN,
      attributes: {
        stale: true,
      },
    });
    dispatch(onToggleBulkDeleteSelectionMode(true));
    onToggleBranchSelectAllInPage(true);
    dispatch(openBulkDeleteBranchesDialog(true));
  }, [dispatch, onToggleBranchSelectAllInPage]);

  const staleFilterSelected: boolean = useQueryParam('status')?.[0] === 'stale';
  const staleBranchFeatureEnabled = useStatsigGate(
    StatsigFeatureKeys.staleBranchesFilter
  );

  const [syncStrategies, setSyncStrategies] = useState<
    SyncStrategy[] | undefined
  >();

  return (
    <Fragment>
      {staleBranchFeatureEnabled && staleFilterSelected && !isLoading && (
        <SectionMessage
          title="Clean Up Stale Branches Easily"
          actions={
            <SectionMessageAction onClick={deleteMultipleBranchesClick}>
              Try it here
            </SectionMessageAction>
          }
          testId="stale-filter-delete-branches"
        >
          <FormattedMessage {...messages.staleBranchesDeleteCTA} />
        </SectionMessage>
      )}
      <RepositoryAccessGuard requiredLevel="write">
        <SyncBranchDialog
          syncStrategies={syncStrategies}
          analyticsParentPage={REPO_EVENT_SOURCES.BRANCHES_SCREEN}
        />
      </RepositoryAccessGuard>
      <SyncStrategyContext.Provider value={setSyncStrategies}>
        <CaptureSentryException exception={exception}>
          <BranchTable
            {...branchTableProps}
            isLoading={isLoading}
            staleFilterSelected={staleFilterSelected}
          />
        </CaptureSentryException>
      </SyncStrategyContext.Provider>
      {!isLoading && <MetricSender metric={BRANCH_LIST_LOAD} />}
    </Fragment>
  );
};

export default withRunPipelineModal(
  memo(BranchList),
  'runPipelineFromBranchList'
);
